// Layout configuration types and pure functions for Views grid mode (#68)

export type GroupName = 'meetings' | 'tasks' | 'notes' | 'files';
export const DEFAULT_GROUP_ORDER: GroupName[] = ['meetings', 'tasks', 'notes', 'files'];

/** Stored as JSON in the `layout_config` column */
export interface LayoutConfig {
  version: 1;
  mode: 'list' | 'grid';
  cards: GridCardConfig[];
  groupOrder?: GroupName[];
  collapsedGroups?: string[];
}

export interface GridCardConfig {
  id: string;
  type: 'meeting_group' | 'task' | 'note' | 'file' | 'annotation';
  ref_id: string;     // meeting instance id | task source_id | note source_id | annotation id
  span: 1 | 2;        // 1 = half-width, 2 = full-width
}

/** Content item shape expected from ViewDetailPage (same as ViewContentItem) */
export interface ContentItem {
  source_type: 'meeting_instance' | 'task' | 'note' | 'attachment';
  source_id: string;
  note_tag_id?: string;
}

/** Annotation shape from ViewDetailPage */
export interface AnnotationItem {
  id: string;
  after_item_id: string | null;
}

/** Meeting group shape for grid layout */
export interface MeetingGroupInfo {
  meeting_instance_id: string;
}

/**
 * Generate a default grid layout from content + annotations.
 *
 * Order:
 *  1. Top-level annotations (after_item_id === null) — full-width
 *  2. Meeting groups (half-width), each followed by its associated annotations (full-width)
 *  3. Tasks (half-width)
 *  4. Notes (half-width)
 */
export function generateDefaultLayout(
  meetingGroups: MeetingGroupInfo[],
  taskItems: ContentItem[],
  noteItems: ContentItem[],
  annotations: AnnotationItem[],
  fileItems: ContentItem[] = [],
): LayoutConfig {
  const cards: GridCardConfig[] = [];

  // Top-level annotations
  for (const ann of annotations.filter(a => a.after_item_id === null)) {
    cards.push({
      id: ann.id,
      type: 'annotation',
      ref_id: ann.id,
      span: 2,
    });
  }

  // Meeting groups + inline annotations
  for (const group of meetingGroups) {
    cards.push({
      id: `mg-${group.meeting_instance_id}`,
      type: 'meeting_group',
      ref_id: group.meeting_instance_id,
      span: 1,
    });

    // Annotations positioned after items in this meeting group
    // (after_item_id is a note_tag_id which belongs to a meeting instance)
    // We include all non-null annotations here since they belong to meeting items
  }

  // Non-top-level annotations (associated with meeting items)
  for (const ann of annotations.filter(a => a.after_item_id !== null)) {
    cards.push({
      id: ann.id,
      type: 'annotation',
      ref_id: ann.id,
      span: 2,
    });
  }

  // Tasks — deduplicate by source_id (a task may appear with multiple tags)
  const seenTasks = new Set<string>();
  for (const task of taskItems) {
    if (seenTasks.has(task.source_id)) continue;
    seenTasks.add(task.source_id);
    cards.push({
      id: `task-${task.source_id}`,
      type: 'task',
      ref_id: task.source_id,
      span: 1,
    });
  }

  // Notes — deduplicate by source_id
  const seenNotes = new Set<string>();
  for (const note of noteItems) {
    if (seenNotes.has(note.source_id)) continue;
    seenNotes.add(note.source_id);
    cards.push({
      id: `note-${note.source_id}`,
      type: 'note',
      ref_id: note.source_id,
      span: 1,
    });
  }

  // Files — deduplicate by source_id
  const seenFiles = new Set<string>();
  for (const file of fileItems) {
    if (seenFiles.has(file.source_id)) continue;
    seenFiles.add(file.source_id);
    cards.push({
      id: `file-${file.source_id}`,
      type: 'file',
      ref_id: file.source_id,
      span: 1,
    });
  }

  return { version: 1, mode: 'grid', cards };
}

/**
 * Reconcile an existing layout with current content.
 *
 * - Keep cards that still have matching content (preserving order + span)
 * - Remove cards whose content no longer exists
 * - Append new content items at the end
 */
export function reconcileLayout(
  existing: LayoutConfig,
  meetingGroups: MeetingGroupInfo[],
  taskItems: ContentItem[],
  noteItems: ContentItem[],
  annotations: AnnotationItem[],
  fileItems: ContentItem[] = [],
): LayoutConfig {
  // Build sets of valid ref_ids by type
  const validIds = new Map<string, Set<string>>();
  validIds.set('meeting_group', new Set(meetingGroups.map(g => g.meeting_instance_id)));

  const taskIds = new Set<string>();
  for (const t of taskItems) taskIds.add(t.source_id);
  validIds.set('task', taskIds);

  const noteIds = new Set<string>();
  for (const n of noteItems) noteIds.add(n.source_id);
  validIds.set('note', noteIds);

  const fileIds = new Set<string>();
  for (const f of fileItems) fileIds.add(f.source_id);
  validIds.set('file', fileIds);

  validIds.set('annotation', new Set(annotations.map(a => a.id)));

  // Filter existing cards — keep those with still-valid content
  const keptCards: GridCardConfig[] = [];
  const keptRefIds = new Map<string, Set<string>>();

  for (const card of existing.cards) {
    const typeIds = validIds.get(card.type);
    if (typeIds && typeIds.has(card.ref_id)) {
      keptCards.push(card);
      if (!keptRefIds.has(card.type)) keptRefIds.set(card.type, new Set());
      keptRefIds.get(card.type)!.add(card.ref_id);
    }
  }

  // Append new items not already in layout
  const newCards: GridCardConfig[] = [];

  for (const ann of annotations) {
    if (keptRefIds.get('annotation')?.has(ann.id)) continue;
    newCards.push({
      id: ann.id,
      type: 'annotation',
      ref_id: ann.id,
      span: 2,
    });
  }

  for (const group of meetingGroups) {
    if (keptRefIds.get('meeting_group')?.has(group.meeting_instance_id)) continue;
    newCards.push({
      id: `mg-${group.meeting_instance_id}`,
      type: 'meeting_group',
      ref_id: group.meeting_instance_id,
      span: 1,
    });
  }

  const seenNewTasks = new Set<string>();
  for (const task of taskItems) {
    if (keptRefIds.get('task')?.has(task.source_id)) continue;
    if (seenNewTasks.has(task.source_id)) continue;
    seenNewTasks.add(task.source_id);
    newCards.push({
      id: `task-${task.source_id}`,
      type: 'task',
      ref_id: task.source_id,
      span: 1,
    });
  }

  const seenNewNotes = new Set<string>();
  for (const note of noteItems) {
    if (keptRefIds.get('note')?.has(note.source_id)) continue;
    if (seenNewNotes.has(note.source_id)) continue;
    seenNewNotes.add(note.source_id);
    newCards.push({
      id: `note-${note.source_id}`,
      type: 'note',
      ref_id: note.source_id,
      span: 1,
    });
  }

  const seenNewFiles = new Set<string>();
  for (const file of fileItems) {
    if (keptRefIds.get('file')?.has(file.source_id)) continue;
    if (seenNewFiles.has(file.source_id)) continue;
    seenNewFiles.add(file.source_id);
    newCards.push({
      id: `file-${file.source_id}`,
      type: 'file',
      ref_id: file.source_id,
      span: 1,
    });
  }

  return {
    version: 1,
    mode: existing.mode,
    cards: [...keptCards, ...newCards],
    groupOrder: existing.groupOrder,
    collapsedGroups: existing.collapsedGroups,
  };
}

/** Parse a layout_config JSON string, returning null if invalid */
export function parseLayoutConfig(json: string | null | undefined): LayoutConfig | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (parsed?.version !== 1) return null;
    if (parsed.mode !== 'list' && parsed.mode !== 'grid') return null;
    if (!Array.isArray(parsed.cards)) return null;
    const config: LayoutConfig = {
      version: 1,
      mode: parsed.mode,
      cards: parsed.cards,
    };
    if (Array.isArray(parsed.groupOrder)) {
      config.groupOrder = parsed.groupOrder.filter((g: unknown) =>
        typeof g === 'string' && DEFAULT_GROUP_ORDER.includes(g as GroupName)
      ) as GroupName[];
    }
    if (Array.isArray(parsed.collapsedGroups)) {
      config.collapsedGroups = parsed.collapsedGroups.filter((g: unknown) =>
        typeof g === 'string' && DEFAULT_GROUP_ORDER.includes(g as GroupName)
      );
    }
    return config;
  } catch {
    return null;
  }
}

/** Validate a layout_config object for server-side storage */
export function validateLayoutConfig(value: unknown): boolean {
  if (value === null) return true; // null clears the config
  if (typeof value !== 'string') return false;
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return false;
    if (parsed.version !== 1) return false;
    if (parsed.mode !== 'list' && parsed.mode !== 'grid') return false;
    if (!Array.isArray(parsed.cards)) return false;

    const validTypes = ['meeting_group', 'task', 'note', 'file', 'annotation'];
    for (const card of parsed.cards) {
      if (typeof card !== 'object' || card === null) return false;
      if (typeof card.id !== 'string') return false;
      if (!validTypes.includes(card.type)) return false;
      if (typeof card.ref_id !== 'string') return false;
      if (card.span !== 1 && card.span !== 2) return false;
    }

    // Validate optional groupOrder
    if (parsed.groupOrder !== undefined) {
      if (!Array.isArray(parsed.groupOrder)) return false;
      if (parsed.groupOrder.length > 4) return false;
      const validGroups = new Set(DEFAULT_GROUP_ORDER);
      const seen = new Set<string>();
      for (const g of parsed.groupOrder) {
        if (typeof g !== 'string' || !validGroups.has(g as GroupName)) return false;
        if (seen.has(g)) return false; // reject duplicates
        seen.add(g);
      }
    }

    // Validate optional collapsedGroups
    if (parsed.collapsedGroups !== undefined) {
      if (!Array.isArray(parsed.collapsedGroups)) return false;
      const validGroups = new Set(DEFAULT_GROUP_ORDER);
      for (const g of parsed.collapsedGroups) {
        if (typeof g !== 'string' || !validGroups.has(g as GroupName)) return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}
